import {css} from '@emotion/react';
import {useBreakpointDown, useBreakpointUp} from '@travel-frontend/core';
import {
  AditionConditional,
  Block,
  Flex,
  Toast,
} from '@travel-frontend/ui-components';
import {useCallback, useEffect, useRef} from 'react';
import useResizeObserver from 'use-resize-observer';

import {FloatingMapButton} from '@/components/FloatingMapButton';
import {HotelsPageLayoutContainer} from '@/components/HotelsPageLayoutContainer';
import {HotelsPageLayoutContent} from '@/components/HotelsPageLayoutContent';
import {HotelsPageLayoutMapOverlay} from '@/components/HotelsPageLayoutMapOverlay';
import {MapWrapper} from '@/components/MapWrapper';
import {useFloatingMapButton} from '@/hooks';
import {ActiveFilters} from '@/modules/common/components/ActiveFilters';
import {AditionSkyscraperBanners} from '@/modules/common/components/Adition';
import {BackToTopButton} from '@/modules/common/components/BackToTopButton';
import {BreadcrumbsHotelsPage} from '@/modules/common/components/Breadcrumbs/BreadcrumbsHotelsPage';
import {ControlBar} from '@/modules/common/components/ControlBar';
import {InactivityLayer} from '@/modules/common/components/InactivityLayer';
import {AFTER_INACTIVITY_SCROLL_TARGET} from '@/modules/common/components/InactivityLayer/consts';
import {HotlineBarFixed} from '@/modules/common/components/Layout/HotlineBarFixed';
import {MapTrigger} from '@/modules/common/components/MapTrigger/MapTrigger';
import {SearchBar} from '@/modules/common/components/SearchBar';
import {Sticky} from '@/modules/common/components/SearchBar/components/Sticky';
import {TrustElements} from '@/modules/common/components/TrustElements';
import {useLiveChat} from '@/modules/common/components/useLiveChat';
import {useFilterOverlay} from '@/modules/common/context/FilterOverlay';
import {useSearchFormOverlay} from '@/modules/common/context/SearchFormOverlay';
import {useLocalSplit} from '@/modules/common/hooks';
import {useFilterChips} from '@/modules/common/hooks/useFilterChips';
import {useSearchParams} from '@/modules/common/hooks/useSearchParams';
import {CpcAd} from '@/modules/hotels/components/CpcAd';
import {Banner} from '@/modules/hotels/components/Layout/components/Banner';
import {FilterLayer} from '@/modules/hotels/components/Layout/components/FilterLayer';
import {Filters} from '@/modules/hotels/components/Layout/components/Filters';
import {SplitViewMapContainer} from '@/modules/hotels/components/Layout/components/SplitViewMapContainer';
import {StickyMapContainer} from '@/modules/hotels/components/Layout/components/StickyMapContainer';
import {List} from '@/modules/hotels/components/List';
import {Header} from '@/modules/hotels/components/List/components/Header';
import {TabRow} from '@/modules/hotels/components/List/components/TabRow/TabRow';
import {MapView, useMapView} from '@/modules/hotels/components/MapView';
import {PromotedHotel} from '@/modules/hotels/components/PromotedHotel';
import {SeaSpotlight} from '@/modules/hotels/components/SeaSpotlight';
import {useFilters} from '@/modules/hotels/hooks/useFilters';
import {usePromotedHotel} from '@/modules/hotels/hooks/usePromotedHotel';
import {useAppSelector} from '@/redux/config';
import {
  areHotelsLoaded as getAreHotelsLoaded,
  getHotelCountFiltered,
  selectAreHotelsLoading,
  selectTranslations,
} from '@/redux/v1/hotels/list/reducer';
import {areRectsIntersecting} from '@/utils/geometry';
import {testId} from '@/utils/testId';

export const Layout = () => {
  const mdDown = useBreakpointDown('md');
  const lgUp = useBreakpointUp('lg');

  const {filters, hasFilters, clear: clearFilters} = useFilters();
  const [searchParams] = useSearchParams();

  const translations = useAppSelector(selectTranslations);
  const areHotelsLoading = useAppSelector(selectAreHotelsLoading);
  const areHotelsLoaded = useAppSelector(getAreHotelsLoaded);
  const hotelCountFiltered = useAppSelector(getHotelCountFiltered);

  const containerElement = useRef<HTMLDivElement | null>(null);
  const searchBarElement = useRef<HTMLDivElement | null>(null);
  const listElement = useRef<HTMLDivElement | null>(null);
  const filtersElement = useRef<HTMLDivElement | null>(null);
  const backToTopElement = useRef<HTMLButtonElement | null>(null);
  const tabRowElement = useRef<HTMLDivElement | null>(null);

  const {isSearchFormOverlayOpen} = useSearchFormOverlay();
  const {isFilterOverlayOpen, closeFilterOverlay} = useFilterOverlay();

  const {
    isWidgetLoaded: isLiveChatWidgetLoaded,
    hideChatWidget,
    restoreChatWidget,
  } = useLiveChat();

  const filterChips = useFilterChips({filters, translations});
  const activeFilterLabels = filterChips.map((filterChip) => filterChip.label);

  const alternativeSearchFormLayoutEnabled =
    useLocalSplit('CAP_292_search_form_sidebar_layout').treatment === 'on' &&
    lgUp;
  const tabRowEnabled =
    useLocalSplit('CAP_310_reorganised_tab_row').treatment === 'on';
  const mapSplitViewEnabled =
    useLocalSplit('CAP_310_map_split_view').treatment === 'on';

  const {height: searchBarHeight = 0} = useResizeObserver({
    ref: searchBarElement,
  });
  const hotlineBarFixed = useResizeObserver();
  const stickyHotlineHeight = hotlineBarFixed.height || 0;
  const stickyTopOffset = searchBarHeight + stickyHotlineHeight;

  const promotedHotel = usePromotedHotel();

  const {
    active: mapViewActive,
    activate: handleMapViewActivate,
    deactivate: handleMapViewDeactivate,
    mode: mapViewMode,
  } = useMapView({
    listElement,
    filtersElement,
    searchBarHeight,
  });
  const floatingMapButton = useFloatingMapButton({
    onPosition: useCallback(({element, margin}) => {
      const {current: backToTopButton} = backToTopElement;
      if (backToTopButton) {
        backToTopButton.style.transform = '';
        if (
          element &&
          areRectsIntersecting(
            element.getBoundingClientRect(),
            backToTopButton.getBoundingClientRect()
          )
        ) {
          // shift
          backToTopButton.style.transform = `translateY(calc(-100% - ${margin}px))`;
        }
      }
    }, []),
  });

  const scrollToTopIfNecessary = useCallback(
    (checkContainer?: null | boolean) => {
      const containerRect = containerElement.current?.getBoundingClientRect();

      if (
        containerRect &&
        !mapViewActive &&
        ((mdDown && !checkContainer) ||
          (checkContainer && containerRect.top < 0))
      ) {
        requestAnimationFrame(() => {
          containerElement.current?.scrollIntoView({behavior: 'instant'});
        });
      }
    },
    [mdDown, mapViewActive]
  );

  const handleSearchFormSubmit = useCallback(() => {
    scrollToTopIfNecessary(true);
  }, [scrollToTopIfNecessary]);

  const handleFilterLayerApply = useCallback(() => {
    closeFilterOverlay();
    scrollToTopIfNecessary();
  }, [closeFilterOverlay, scrollToTopIfNecessary]);

  const handleFilterLayerReset = useCallback(() => {
    clearFilters();
    scrollToTopIfNecessary();
  }, [clearFilters, scrollToTopIfNecessary]);

  const handleFilterChange = useCallback(() => {
    scrollToTopIfNecessary();
  }, [scrollToTopIfNecessary]);

  const filtersNode = (
    <Filters
      ref={filtersElement}
      onChange={handleFilterChange}
      noBorders={mapViewActive && mapSplitViewEnabled}
    />
  );

  const searchNode = (
    <SearchBar
      ref={searchBarElement}
      alternativeLayout={alternativeSearchFormLayoutEnabled}
      overlayOpen={isSearchFormOverlayOpen}
      activeFilterCount={filterChips.length}
      stickyOffset={stickyHotlineHeight}
      onSubmit={handleSearchFormSubmit}
      tabRowEnabled={tabRowEnabled}
    />
  );

  const mapTrigger = (
    <MapTrigger
      mapView={mapViewActive}
      openMapView={handleMapViewActivate}
      closeMapView={handleMapViewDeactivate}
    />
  );

  useEffect(() => {
    if (!isLiveChatWidgetLoaded) {
      return;
    }

    if (mapViewActive) {
      hideChatWidget();
    } else {
      restoreChatWidget();
    }
  }, [
    isLiveChatWidgetLoaded,
    mapViewActive,
    hideChatWidget,
    restoreChatWidget,
  ]);

  if (mapViewActive && mapSplitViewEnabled && lgUp) {
    return (
      <SplitViewMapContainer>
        <MapWrapper>
          {!alternativeSearchFormLayoutEnabled && searchNode}
          <MapView
            filters={filtersNode}
            searchNode={searchNode}
            mapTrigger={mapTrigger}
            alternativeLayout={alternativeSearchFormLayoutEnabled}
            splitView={mapSplitViewEnabled}
            mode="sticky"
            canShowNoResultsNotification={hasFilters}
            travellerCount={searchParams.rooms?.travellerCount ?? 1}
            onClose={handleMapViewDeactivate}
            onFilterReset={clearFilters}
            promotedHotel={promotedHotel.offer}
            promotedHotelId={promotedHotel.id}
            promotedHotelEnabled={promotedHotel.enabled}
            promotedHotelStatus={promotedHotel.status}
          />
        </MapWrapper>
      </SplitViewMapContainer>
    );
  }

  return (
    <HotelsPageLayoutContainer ref={containerElement}>
      <SeaSpotlight />
      <Block id={AFTER_INACTIVITY_SCROLL_TARGET} />
      {!alternativeSearchFormLayoutEnabled && searchNode}
      <HotelsPageLayoutContent
        ref={floatingMapButton.visibilityRef}
        top={
          !alternativeSearchFormLayoutEnabled &&
          mdDown && <BreadcrumbsHotelsPage marginBottom="16px" />
        }
        main={
          <Flex
            ref={floatingMapButton.positionRef}
            data-testid={testId('hotellist')}
            minHeight="100%"
            spacing="pages/small"
          >
            <Banner />
            {promotedHotel.enabled && (
              <PromotedHotel
                id={promotedHotel.id}
                hotel={promotedHotel.offer}
                status={promotedHotel.status}
                isVisuallyHidden={mapViewActive}
              />
            )}
            {tabRowEnabled && mdDown && (
              <Sticky
                name="TabRow"
                stickyOffset={stickyTopOffset}
                ref={tabRowElement}
                injectedStyles={css`
                  margin: 0 -8px;
                `}
              >
                <TabRow
                  mapView={mapViewActive}
                  openMapView={handleMapViewActivate}
                  closeMapView={handleMapViewDeactivate}
                />
              </Sticky>
            )}
            <Header
              activeFilterCount={filterChips.length}
              activeFilterLabels={activeFilterLabels}
              sortingControlEnabled={!tabRowEnabled || !mdDown}
            />
            {!mdDown && areHotelsLoaded && filterChips.length > 0 && (
              <ActiveFilters
                filterChips={filterChips}
                onClickReset={clearFilters}
              />
            )}
            <List
              // Make sure ref is stable to not break List memoization
              ref={listElement}
              // We're hiding visually instead of unmounting to not loose local state and re-trigger effects.
              // Also all the data fetching happens inside the component, so filters wouldn't work if we unmount.
              // The downside is that we re-render the whole list on filter while the map view is open
              mapViewActive={mapViewActive}
              visuallyHidden={mapViewActive && mapViewMode === 'sticky'}
            />
            {hotelCountFiltered > 0 &&
              ((mdDown && !tabRowEnabled) ||
                (!mdDown && !mapSplitViewEnabled)) && (
                <FloatingMapButton
                  ref={floatingMapButton.buttonRef}
                  visible={!mapViewActive && floatingMapButton.canBeShown}
                  style={floatingMapButton.buttonStyles}
                  onClick={handleMapViewActivate}
                />
              )}
            {mapViewActive && mapViewMode === 'sticky' && (
              <StickyMapContainer
                style={{'--sticky-offset': `${stickyTopOffset}px`}}
              >
                <MapView
                  filters={filtersNode}
                  mode="sticky"
                  canShowNoResultsNotification={hasFilters}
                  travellerCount={searchParams.rooms?.travellerCount ?? 1}
                  onClose={handleMapViewDeactivate}
                  tabRowEnabled={tabRowEnabled}
                  onFilterReset={clearFilters}
                  promotedHotel={null}
                  promotedHotelId=""
                  promotedHotelEnabled={false}
                  promotedHotelStatus="not-executed"
                />
              </StickyMapContainer>
            )}
            <CpcAd
              id="cpcAdHotelsFooter"
              visuallyHidden={mapViewActive && mapViewMode === 'sticky'}
            />
          </Flex>
        }
        aside={
          <Block>
            {lgUp && <BreadcrumbsHotelsPage marginBottom="8px" />}
            <Flex spacing="16px">
              {alternativeSearchFormLayoutEnabled && searchNode}
              {mapSplitViewEnabled && (
                <MapTrigger
                  mapView={mapViewActive}
                  openMapView={handleMapViewActivate}
                  closeMapView={handleMapViewDeactivate}
                />
              )}
              <Flex spacing="16px">
                {filtersNode}
                <CpcAd id="cpcAdHotelsSidebar" />
              </Flex>
            </Flex>
          </Block>
        }
        banners={
          <>
            {!mdDown && (
              <AditionConditional>
                <AditionSkyscraperBanners stickyOffset={stickyTopOffset} />
              </AditionConditional>
            )}
            <TrustElements />
          </>
        }
      />
      {mdDown && (
        <HotelsPageLayoutMapOverlay
          controlBar={
            mapViewActive &&
            mapViewMode === 'overlay' && (
              <>
                <ControlBar
                  // Make space for the sticky hotline bar in case it is visible
                  // @TODO Refactor composition of the map view
                  css={css`
                    margin-top: ${stickyHotlineHeight}px;
                  `}
                  travelType={searchParams.travelType}
                  activeFilterCount={filterChips.length}
                  tabRowEnabled={tabRowEnabled}
                />
                {tabRowEnabled && (
                  <TabRow
                    mapView={mapViewActive}
                    openMapView={handleMapViewActivate}
                    closeMapView={handleMapViewDeactivate}
                  />
                )}
              </>
            )
          }
          map={
            mapViewActive &&
            mapViewMode === 'overlay' && (
              <MapView
                filters={filtersNode}
                splitView={mapSplitViewEnabled}
                mode="overlay"
                canShowNoResultsNotification={hasFilters}
                travellerCount={searchParams.rooms.travellerCount}
                onClose={handleMapViewDeactivate}
                onFilterReset={clearFilters}
                tabRowEnabled={tabRowEnabled}
                promotedHotel={promotedHotel.offer}
                promotedHotelId={promotedHotel.id}
                promotedHotelEnabled={promotedHotel.enabled}
                promotedHotelStatus={promotedHotel.status}
              />
            )
          }
          isMapVisible={mapViewActive}
        />
      )}
      <FilterLayer
        isOpen={isFilterOverlayOpen}
        isLoading={areHotelsLoading}
        onApply={handleFilterLayerApply}
        onReset={handleFilterLayerReset}
        onClose={closeFilterOverlay}
      >
        {filtersNode}
      </FilterLayer>
      {!mapViewActive && <BackToTopButton ref={backToTopElement} />}
      <HotlineBarFixed ref={hotlineBarFixed.ref} />
      <InactivityLayer />
      <Toast />
    </HotelsPageLayoutContainer>
  );
};
